import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function TermsGate({ plan, onClose }) {
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const open = Boolean(plan);

  const confirm = async () => {
    if (!agreed) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plan.lookup_key, origin_url: window.location.origin, accepted_terms: true });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  const close = () => { if (!busy) { setAgreed(false); onClose(); } };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="terms-gate">
          <div className="absolute inset-0 bg-[#FAF6EF]/80 backdrop-blur-sm" onClick={close} />
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className="relative glass rounded-2xl w-full max-w-md p-7 sm:p-8 shadow-[0_8px_32px_rgba(58,46,37,0.15)]">
            <button onClick={close} className="absolute top-4 right-4 text-[#8C7562] hover:text-[#3A2E25]" data-testid="terms-gate-close" aria-label="Close"><X size={18} /></button>
            <div className="w-11 h-11 rounded-xl bg-[#D4A0B4]/15 border border-[#D4A0B4]/40 flex items-center justify-center mb-5">
              <ShieldCheck size={18} className="text-[#D4A0B4]" />
            </div>
            <span className="eyebrow">Before checkout</span>
            <h3 className="f-display text-2xl font-medium tracking-tight mt-2 mb-1">{plan.name}</h3>
            <p className="text-sm text-[#8C7562] leading-relaxed mb-6">{plan.price}{plan.billing === "monthly" ? " per month · cancel anytime" : " · one-time"}</p>
            <label className="flex items-start gap-3 rounded-xl border border-[#E8DCCB] bg-[#FFFDF7] p-4 cursor-pointer hover:border-[#A8785A] transition-colors" data-testid="terms-checkbox-label">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="terms-checkbox"
                className="mt-0.5 w-4 h-4 accent-[#A8785A] shrink-0" />
              <span className="text-sm text-[#3A2E25] leading-relaxed">
                I have read and agree to the{" "}
                <Link to="/policies" target="_blank" rel="noreferrer" className="text-[#A8785A] font-semibold underline underline-offset-2" data-testid="terms-policy-link">
                  Terms & Policies
                </Link>
                , including the refund and cancellation policy.
              </span>
            </label>
            <button onClick={confirm} disabled={!agreed || busy} className="btn-primary w-full justify-center mt-5 disabled:opacity-50" data-testid="terms-continue-btn">
              <Lock size={14} /> {busy ? "Opening secure checkout…" : "Continue to secure checkout"}
            </button>
            <p className="text-[11px] text-[#B9A896] text-center mt-3">Payments are processed securely by Stripe.</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
